/**
 * Top app bar, modelled on the Auth0 dashboard: org/tenant selector + region on
 * the left, a command-palette search in the middle, and the action cluster on
 * the right — "Discuss your needs", Documentation, AI, notifications, language,
 * wallet, theme and the user menu. Each control is its own component under
 * `components/shell/` so this file stays a thin layout.
 */
import * as React from "react";
import { MessageSquareText, BookOpen, Sparkles, Moon, Sun } from "lucide-react";
import { useTheme } from "@authe/ui-core/theme";
import { Button } from "@authe/ui-core";
import { EXTERNAL_LINKS } from "@/config";
import { useAskAi } from "@/app/ask-ai-shell";
import { OrgTenantMenu } from "./OrgTenantMenu";
import { HeaderSearch } from "./HeaderSearch";
import { NotificationsMenu } from "./NotificationsMenu";
import { LanguageMenu } from "./LanguageMenu";
import { WalletButton } from "./WalletButton";
import { ProfileMenu } from "./ProfileMenu";
import { Hint } from "./Hint";
import { ChatDrawer } from "./ChatDrawer";

export function Header(): React.ReactElement {
  const { mode, setMode, resolvedMode } = useTheme();
  const [chatOpen, setChatOpen] = React.useState(false);
  const { openAskAi } = useAskAi();

  return (
    <header className="flex h-14 items-center gap-3 border-b border-border bg-background px-4">
      <div className="flex shrink-0 items-center">
        <OrgTenantMenu />
      </div>

      <div className="flex flex-1 justify-center">
        <HeaderSearch />
      </div>

      <div className="flex shrink-0 items-center gap-1.5">
        <Button
          variant="default"
          size="sm"
          className="hidden lg:inline-flex"
          onClick={() => setChatOpen(true)}
        >
          <MessageSquareText className="h-4 w-4" />
          Discuss your needs
        </Button>
        <ChatDrawer open={chatOpen} onOpenChange={setChatOpen} />

        <Hint label="Documentation">
          <Button asChild variant="ghost" size="icon" aria-label="Documentation">
            <a href={EXTERNAL_LINKS.docs} target="_blank" rel="noreferrer">
              <BookOpen className="h-4 w-4" />
            </a>
          </Button>
        </Hint>

        <Hint label="Ask AI">
          <Button variant="ghost" size="icon" aria-label="Ask AI" onClick={openAskAi}>
            <Sparkles className="h-4 w-4" />
          </Button>
        </Hint>

        <NotificationsMenu />
        <LanguageMenu />

        <WalletButton />

        <Hint label="Toggle theme">
          <Button
            variant="ghost"
            size="icon"
            aria-label="Toggle theme"
            onClick={() => setMode(mode === "dark" ? "light" : "dark")}
          >
            {resolvedMode === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </Button>
        </Hint>

        <ProfileMenu />
      </div>
    </header>
  );
}
